﻿import { type JSX, useContext, useEffect, useRef, useState } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type {
  EmployeeTypeResponse,
  PlanningEntityIdentifier,
  PlanningEntityType,
  TransportationMode
} from 'generated';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';

import type { Event } from '../../types';
import type { EditAppointmentType } from 'RestAPIs/activities/types';

import { BLUE_SECONDARY, ORANGE, PRIMARY, WHITE } from 'constants/colors';

import { shouldShowPopoverDialogAtom } from '../../atoms';

import { Box, Skeleton, type SxProps } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import PopoverWarningDialog from '../../components/PopoverWarningDialog/PopoverWarningDialog';

import { ScheduleContextMenu } from '../../components';
import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';
import { formatByLocale } from 'utils/date';
import { brightness } from 'utils/misc';

const StyledStickyHeader = styled(Box)`
  z-index: 35;
  position: sticky;
  top: 0;
  padding: 0 10px;
  padding-right: 4px;
  display: flex;
  align-items: center;
`;

const IconsSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const EmployeeTypeName = styled(Box)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 112px;
`;

const TimeframeChip = ({
  showChip = false,
  label = '',
  sx = {}
}: {
  showChip: boolean | undefined;
  label?: string | null;
  sx?: SxProps;
}) => {
  'use memo';
  if (!showChip) return <>{label}</>;
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        height: '15px',
        padding: '1px 5px',
        borderRadius: '20px',
        color: WHITE,
        backgroundColor: ORANGE,
        ...sx
      }}
    >
      {label}
    </Box>
  );
};

type StickyHeaderPragmaticProps = {
  id: string;
  appointmentGroupingEntityId?: string;
  droppable?: {
    droppableId?: string;
    planningEntityType: PlanningEntityType;
    planningEntityId: string;
    planningEntityIdentifier?: PlanningEntityIdentifier;
    scheduleStart: string;
  };
  name?: string;
  headerName?: string | JSX.Element;
  end: string;
  employeeId?: number;
  employeeType?: EmployeeTypeResponse;
  onConnectClick?: () => void;
  onConnectScheduleClick?: (editAppointmentType: EditAppointmentType) => void;
  onDialogOpen?: (isOpen: boolean) => void;
  onContextMenu?: (open: boolean) => void;
  events?: Event[];
  readOnly?: boolean;
  start: string;
  date: string;
  showTimePeriod?: boolean;
  transportationMode?: TransportationMode;
  startsTooEarly?: boolean;
  endsTooLate?: boolean;
  isLoading?: boolean;
  isAssignableToEmployee?: boolean;
  hasScheduleWithMultipleDuties?: boolean;
};

export default function StickyHeaderPragmatic({
  id,
  appointmentGroupingEntityId,
  droppable,
  name,
  headerName,
  employeeId,
  employeeType,
  onConnectClick,
  onConnectScheduleClick,
  onContextMenu,
  onDialogOpen,
  events,
  readOnly,
  transportationMode,
  date,
  start,
  end,
  startsTooEarly,
  endsTooLate,
  isLoading,
  isAssignableToEmployee,
  hasScheduleWithMultipleDuties
}: StickyHeaderPragmaticProps) {
  'use memo';
  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );
  const {
    isLoading: isLoadingConfirmation,
    onClose,
    onHandleAppointmentConfirmation
  } = useContext(PopOverConfirmationContext);

  const {
    droppableId,
    planningEntityType,
    planningEntityId,
    planningEntityIdentifier,
    scheduleStart
  } = droppable || {
    id: '',
    planningEntityType: '' as PlanningEntityType,
    planningEntityId: '',
    planningEntityIdentifier: {} as PlanningEntityIdentifier,
    scheduleStart: ''
  };

  const disabled: boolean = !droppable || !droppable.droppableId;
  const isSnapshot = !start && !end;

  const ref = useRef<HTMLElement>(null);
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || disabled) return;

    return dropTargetForElements({
      element: el,
      getData: () => ({
        planningEntityType,
        planningEntityId,
        planningEntityIdentifier,
        scheduleStart,
        isDroppablePanelHeader: true
      }),
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false)
    });
  }, [
    disabled,
    planningEntityId,
    planningEntityType,
    planningEntityIdentifier,
    scheduleStart,
    droppableId
  ]);

  const isRosterEmployeePerspective =
    planningEntityType === 'EMPLOYEE' || planningEntityType === 'EMPLOYMENT';

  if (!readOnly && isLoading) {
    return (
      <>
        <Skeleton
          variant="rounded"
          width="100%"
          height="40px"
          sx={{
            minHeight: '40px',
            borderRadius: '6px',
            marginBottom: isSnapshot ? '8px' : 'initial',
            ...(isRosterEmployeePerspective && {
              borderRadius: '6px 6px 0 0'
            })
          }}
        />
        {!readOnly &&
          (planningEntityType === 'ROUTE' || isRosterEmployeePerspective) && (
            <PopoverWarningDialog
              open={shouldShowPopoverDialog.id == id && !isLoadingConfirmation}
              isMultipleSelected
              onClose={() => {
                onClose && onClose();
                setShouldShowPopoverDialog({ id: '' });
              }}
              onClickAppointment={(
                editAppointmentType: EditAppointmentType
              ) => {
                onHandleAppointmentConfirmation &&
                  onHandleAppointmentConfirmation(editAppointmentType);
                setShouldShowPopoverDialog({ id: '' });
              }}
            >
              <Box />
            </PopoverWarningDialog>
          )}
      </>
    );
  }

  return (
    <StyledStickyHeader
      ref={ref}
      sx={{
        minHeight: '40px',
        boxSizing: 'border-box',
        color: isOver ? PRIMARY : 'inherit',
        border: `2px solid ${isOver ? PRIMARY : 'transparent'}`,
        backgroundColor: isOver ? brightness(PRIMARY, 90) : BLUE_SECONDARY,
        borderRadius: '6px',
        marginBottom: isSnapshot ? '8px' : 'initial',
        ...(isRosterEmployeePerspective && {
          borderRadius: '6px 6px 0 0'
        })
      }}
    >
      <IconsSection>
        <Box display="flex" alignItems="center">
          <TimeframeChip
            showChip={startsTooEarly}
            label={start && formatByLocale(new Date(start), 'HH:mm')}
          />
          {start && end && <Box m="0 3px">-</Box>}
          <TimeframeChip
            showChip={endsTooLate}
            label={end && formatByLocale(new Date(end), 'HH:mm')}
          />
        </Box>
        {employeeType && (
          <EmployeeTypeName>{employeeType.name}</EmployeeTypeName>
        )}
      </IconsSection>

      <Box
        sx={{
          fontSize: 14,
          marginLeft: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        {headerName}
        {hasScheduleWithMultipleDuties && (
          <Icon type="triangle-exclamation" fontSize="small" color="error" />
        )}
      </Box>
      <Box
        display="flex"
        gap={1}
        sx={{
          position: 'absolute',
          right: 0
        }}
      >
        {!readOnly &&
          (planningEntityType === 'ROUTE' || isRosterEmployeePerspective) && (
            <PopoverWarningDialog
              open={id === shouldShowPopoverDialog.id && !isLoadingConfirmation}
              isMultipleSelected
              onClose={() => {
                onClose && onClose();
                setShouldShowPopoverDialog({ id: '' });
              }}
              onClickAppointment={(
                editAppointmentType: EditAppointmentType
              ) => {
                onHandleAppointmentConfirmation &&
                  onHandleAppointmentConfirmation(editAppointmentType);
                setShouldShowPopoverDialog({ id: '' });
              }}
            >
              <Box>
                <ScheduleContextMenu
                  appointmentGroupingEntityId={appointmentGroupingEntityId}
                  transportationMode={transportationMode}
                  onConnectClick={onConnectClick}
                  onConnectScheduleClick={onConnectScheduleClick}
                  onDialogOpen={onDialogOpen}
                  appointmentGroupingStart={date}
                  appointmentGroupingName={name}
                  events={events}
                  employeeId={employeeId}
                  onOpen={(open: boolean) => onContextMenu?.(open)}
                  isAssignableToEmployee={!!isAssignableToEmployee}
                  scheduleId={planningEntityId}
                  planningEntityIdentifier={planningEntityIdentifier}
                />
              </Box>
            </PopoverWarningDialog>
          )}
      </Box>
    </StyledStickyHeader>
  );
}
